/**
 * The launched GroundTruth IO workspace: one campaign shell with two views,
 * the Campaign grid (actual GT state, read-only) and the Activity log (the
 * page's existing activity, rendered by the host below this component).
 *
 *   - The grid shows the server's last published GT snapshot, labelled with
 *     when it was read: complete, or partial with the ad group details or
 *     creative lists GT would not return left blank and marked red. A cold
 *     cache triggers one read-only fetch, and after that GT is re-read only
 *     on "Refresh GT data" (Q3, AC-13/14).
 *   - Read-only throughout: no edit, upload, removal, bulk fix, re-check or
 *     approve control is wired, and the detail panel opens read-only (AC-06).
 *   - The "Activity log" tab sits beside the grid's column groups in every
 *     state — loading, error, launching, failed — and switching keeps the
 *     grid's tab, search and scroll for the visit (AC-04).
 */
import React, { useMemo, useState } from 'react';
import ReviewGrid from '../CampaignLaunchWorkflow/ReviewGrid';
import { fmtCellValue } from '../CampaignLaunchWorkflow/clwFormatters';
import useGtLiveState from './useGtLiveState';
import { buildGtLiveRows, gtLiveGroupCols, GT_LIVE_GROUP_KEYS, gtLiveTiles } from './gtLiveRows';
import { formatGtTimestamp, relativeAge } from './gtTime';
import { isGtLaunched } from './gtLaunchState';
import '../CampaignLaunchWorkflow/CampaignLaunchWorkflow.css';
import './GtLaunchedWorkspace.css';

const NO_EDITS = new Set();
const noop = () => {};

/** Tabs for the states with no grid to show: same look, same Activity switch. */
function StateTabs({ view, onViewChange }) {
  return (
    <div className="clw-cg-bar gtw-state-tabs">
      <div className={`clw-cg-tab ${view === 'grid' ? 'active' : ''}`} onClick={() => onViewChange('grid')}>Campaign grid</div>
      <div className={`clw-cg-tab clw-cg-tab-trailing ${view === 'activity' ? 'active' : ''}`}
        data-testid="clw-trailing-tab" onClick={() => onViewChange('activity')}>Activity log</div>
    </div>
  );
}

function Notice({ kind = 'info', title, children, action }) {
  return (
    <div className={`gtw-notice gtw-notice-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <div className="gtw-notice-body">
        {title && <strong>{title}</strong>}
        {children && <span>{children}</span>}
      </div>
      {action}
    </div>
  );
}

/** Which resources GT did not return, by ad group, for the partial-read notice. */
function unreadSummary(data) {
  const names = new Map((data.adGroups || []).map((a) => [a.id, a.name]));
  const parts = (data.readErrors || []).map((f) => {
    const name = names.get(f.adGroupId);
    // A 200 GT answered with an unusable body or an unverifiable list is not an HTTP error.
    const why = f.status >= 400 ? `HTTP ${f.status}` : 'unusable answer';
    return `${f.resource === 'creatives' ? 'Creatives' : 'Settings'} of ad group ${name ? `"${name}" (${f.adGroupId})` : f.adGroupId}: ${why}`;
  });
  return `${parts.join('; ')}. Those cells are blank and marked red; everything else is what GroundTruth returned.`;
}

export default function GtLaunchedWorkspace({
  campaignId, accountId, userId, launchState, view, onViewChange, campaignName,
}) {
  const enabled = isGtLaunched(launchState);
  const {
    live, data, loading, loadError, requestError, pollExpired, refreshing, refresh,
  } = useGtLiveState({ campaignId, accountId, userId, enabled });

  // Grid view state lives here so it survives switches to Activity.
  const [activeGroup, setActiveGroup] = useState('overview');
  const [gridSearch, setGridSearch] = useState('');
  const [detailRowId, setDetailRowId] = useState(null);
  const [detailCellField, setDetailCellField] = useState(null);

  const groupCols = useMemo(() => gtLiveGroupCols(), []);
  const rows = useMemo(() => buildGtLiveRows(data), [data]);
  const visibleRows = useMemo(() => {
    const q = gridSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.name, r.adgroup_id, r.product, r.package_name, r.targeting_product]
      .some((v) => String(v ?? '').toLowerCase().includes(q)));
  }, [rows, gridSearch]);
  const tiles = gtLiveTiles(data);
  const identity = live?.identity;
  const snapshot = live?.snapshot;
  const effectiveGroup = groupCols[activeGroup] ? activeGroup : 'overview';
  const detailRow = rows.find((r) => r.rowId === detailRowId) || null;

  const accessError = live?.accessError;
  // Once the server has answered, its launch state is the authority.
  const serverState = identity?.state;
  const serverLaunched = serverState === 'launched';
  const gridReady = enabled && serverLaunched && Boolean(data) && !accessError;
  const partial = launchState === 'launched_partial' || Boolean(identity?.partial);
  const refreshButton = enabled && serverLaunched && (
    <button type="button" className="clw-btn clw-btn-secondary clw-btn-sm gtw-refresh"
      onClick={refresh} disabled={refreshing} data-testid="gt-refresh"
      title="Read this campaign's current state from GroundTruth (read-only)">
      {refreshing ? 'Refreshing GT data…' : 'Refresh GT data'}
    </button>
  );

  // What the page is showing and how fresh it is.
  let sourceLine = null;
  if (enabled && snapshot) {
    sourceLine = (
      <span data-testid="gt-source-line">
        Cached GT data — Last refreshed {formatGtTimestamp(snapshot.fetchCompletedAt)}
        <span className="gtw-age"> ({relativeAge(snapshot.fetchCompletedAt)})</span>
      </span>
    );
  } else if (enabled && (loading || refreshing)) {
    sourceLine = <span data-testid="gt-source-line">Reading this campaign from GroundTruth for the first time…</span>;
  }

  // The panel shown in place of the grid when there is no grid to show.
  let statePanel = null;
  if (launchState === 'launching') {
    statePanel = (
      <Notice kind="info" title="GroundTruth launch in progress.">
        The grid appears once GroundTruth confirms the campaign was created. Progress is in the Activity log.
      </Notice>
    );
  } else if (launchState === 'launch_failed') {
    statePanel = (
      <Notice kind="error" title="The GroundTruth launch did not complete.">
        There is no launched campaign to show. The Activity log has what happened.
      </Notice>
    );
  } else if (serverState === 'error') {
    statePanel = <Notice kind="error" title="This campaign's GT mapping needs attention.">{identity.message}</Notice>;
  } else if (serverState && !serverLaunched) {
    // The messages and the server disagree (e.g. the launch message was
    // deleted): trust the server, and say why there is no grid.
    statePanel = (
      <Notice kind="error" title="No successful GroundTruth launch is recorded for this campaign.">
        The Activity log has what happened.
      </Notice>
    );
  } else if (accessError) {
    statePanel = (
      <Notice kind="error" title="GroundTruth campaign not accessible." action={refreshButton}>
        {accessError.message} The last cached copy is not shown as the current campaign.
      </Notice>
    );
  } else if (live?.configError) {
    statePanel = <Notice kind="error" title="GT data unavailable.">{live.configError.message}</Notice>;
  } else if (loadError) {
    statePanel = <Notice kind="error" title="Could not load the GT campaign state.">{loadError}</Notice>;
  } else if (enabled && !data && live?.refreshError && !refreshing) {
    statePanel = (
      <Notice kind="error" title="Could not read the campaign from GroundTruth." action={refreshButton}>
        {live.refreshError.message}
      </Notice>
    );
  } else if (enabled && !data && live?.snapshotUnreadable && !refreshing) {
    statePanel = (
      <Notice kind="error" title="The cached GT data could not be read." action={refreshButton}>
        Refresh to read the campaign from GroundTruth again.
      </Notice>
    );
  } else if (enabled && !data && (loading || refreshing || !live)) {
    statePanel = (
      <Notice kind="info" title="Loading GT data…">
        Reading the campaign, ad groups and creatives from GroundTruth. This can take a minute for large campaigns.
      </Notice>
    );
  } else if (enabled && !data) {
    // Nothing cached and nothing running (the automatic first read was
    // declined, e.g. right after a failure): offer the read, never spin.
    statePanel = (
      <Notice kind="info" title="No GT data has been read yet." action={refreshButton}>
        Refresh to read the campaign from GroundTruth.
      </Notice>
    );
  }

  return (
    <section className="gt-launched-workspace" data-testid="gt-launched-workspace">
      <div className="gtw-head">
        <div className="gtw-ident">
          <span className="gtw-label">GroundTruth campaign</span>
          {identity?.gtCampaignId ? (
            <a href={identity.gtCampaignUrl} target="_blank" rel="noopener noreferrer" className="gtw-gtid">
              {identity.gtCampaignId}
            </a>
          ) : <span className="gtw-gtid">—</span>}
          {tiles?.status && (
            <span className={`gtw-status gtw-status-${tiles.status.toLowerCase().replace(/\s+/g, '-')}`}
              data-testid="gt-campaign-status">GT: {tiles.status}</span>
          )}
          {data?.campaign?.name && data.campaign.name !== campaignName && (
            <span className="gtw-gtname" title="Campaign name in GroundTruth">{data.campaign.name}</span>
          )}
        </div>
        <div className="gtw-source">{sourceLine}</div>
        {gridReady && refreshButton}
      </div>

      {gridReady && partial && (
        <Notice kind="warn" title="The GroundTruth launch reported errors.">
          {identity?.launchErrors ? `${identity.launchErrors} error${identity.launchErrors === 1 ? '' : 's'}. ` : ''}
          The grid shows what GroundTruth has now; the Activity log has what failed.
        </Notice>
      )}
      {gridReady && data?.partial && (
        <Notice kind="warn" title="GroundTruth did not return part of this campaign." action={refreshButton}>
          <span data-testid="gt-unread-summary">{unreadSummary(data)}</span>
        </Notice>
      )}
      {gridReady && live?.refreshError && (
        <Notice kind="warn" title="Refresh failed; showing cached data from"
          action={refreshButton}>
          {formatGtTimestamp(snapshot?.fetchCompletedAt)}. {live.refreshError.message}
        </Notice>
      )}
      {gridReady && live?.configError && <Notice kind="warn" title="Refresh unavailable.">{live.configError.message}</Notice>}
      {requestError && <Notice kind="error" title="Could not start the refresh.">{requestError}</Notice>}
      {pollExpired && (
        <Notice kind="warn" title="The refresh is taking longer than expected.">
          It keeps running on the server; reload later to see the result.
        </Notice>
      )}

      {gridReady && tiles && (
        <div className="gtw-tiles" data-testid="gt-tiles">
          <div className="gtw-tile"><span className="gtw-tile-n">{tiles.adGroups}</span><span>Ad groups in GT</span></div>
          <div className="gtw-tile">
            <span className="gtw-tile-n">{tiles.creatives}</span>
            <span>
              {tiles.creativesUnreadAdGroups
                ? `Creatives read from GT (${tiles.creativesUnreadAdGroups} ad group${tiles.creativesUnreadAdGroups === 1 ? '' : 's'} not returned)`
                : 'Creatives in GT'}
            </span>
          </div>
          <div className="gtw-tile"><span className="gtw-tile-n">{fmtCellValue(tiles.budget, 'cur')}</span><span>GT campaign budget</span></div>
          <div className="gtw-tile">
            <span className="gtw-tile-n">{fmtCellValue(tiles.startDate, 'date')} – {fmtCellValue(tiles.endDate, 'date')}</span>
            <span>GT flight</span>
          </div>
        </div>
      )}

      <div className={`clw gtw-grid${gridReady ? '' : ' gtw-grid-states'}${view === 'activity' ? ' gtw-grid-parked' : ''}${detailRow && view !== 'activity' ? ' has-dpanel' : ''}`}>
        {gridReady ? (
          <div className={`clw-pane review active${detailRow ? ' has-dpanel' : ''}`}>
            <ReviewGrid
              reviewRows={rows}
              visibleRows={visibleRows}
              getRowValue={(row, field) => row[field]}
              editedRows={{}}
              groupCols={groupCols}
              groupKeys={GT_LIVE_GROUP_KEYS}
              cols={groupCols[effectiveGroup]}
              effectiveActiveGroup={effectiveGroup}
              isGt
              activeGroup={activeGroup}
              setActiveGroup={setActiveGroup}
              gridSearch={gridSearch}
              setGridSearch={setGridSearch}
              filterIssuesOnly={false}
              setFilterIssuesOnly={noop}
              showIssuesFilter={false}
              showAgentButton={false}
              detailRow={detailRow}
              detailCellField={detailCellField}
              setDetailRowId={setDetailRowId}
              setDetailCellField={setDetailCellField}
              editingCell={null}
              setEditingCell={noop}
              editingDraft=""
              setEditingDraft={noop}
              commitCellEdit={noop}
              cancelCellEdit={noop}
              editableFields={NO_EDITS}
              fieldEditors={{}}
              ctsReview={null}
              pendingDeletions={{}}
              toggleListDeletion={noop}
              alreadyLaunched
              campaignName={data?.campaign?.name || campaignName || 'campaign'}
              // The export carries the snapshot it was read from, not just the export time.
              exportSourceName={`${data?.campaign?.name || campaignName || 'campaign'} GT as of ${
                String(snapshot?.fetchCompletedAt || '').slice(0, 16).replace(/[:T]/g, '-')}`}
              aiOpen={false}
              setAiOpen={noop}
              widthsKey="clw_col_widths_gt_launched"
              stickyF2Label="Ad group"
              trailingTab={{
                label: 'Activity log',
                active: view === 'activity',
                onSelect: () => onViewChange('activity'),
                onLeave: () => onViewChange('grid'),
              }}
              summaryBar={(
                <div className="clw-gsumbar">
                  <span><strong>{visibleRows.length}</strong> of {rows.length} GT ad groups · read-only</span>
                </div>
              )}
            />
          </div>
        ) : (
          <>
            <StateTabs view={view} onViewChange={onViewChange} />
            {view !== 'activity' && statePanel && <div className="gtw-state">{statePanel}</div>}
          </>
        )}
      </div>
    </section>
  );
}
